import { maybe, Maybe } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import {
  SYSTEM_SETTINGS_WIRE_KEY,
  toSystemSettingsWire
} from 'store/v1/auth/systemSettings.bridge';
import { ReduxProxy } from 'v5/api/_common/reduxProxy';
import {
  isSettingName,
  SettingName
} from 'v5/api/system-settings/settingCatalog';
import {
  SystemSettingsApi,
  SystemSetting
} from 'v5/api/system-settings/system-settings.api';
import { AppScopeIdentity } from 'v5/core/app-scope/appScope.types';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import {
  resolveAllSettings,
  resolveSettingValue
} from './resolveSystemSettings';
import { SystemSettingsService } from './SystemSettingsService';

export function systemSettingsQueryKey(organizationId: string) {
  return ['system-settings-v5', organizationId] as const;
}

export function createSystemSettingsService(config: {
  api: SystemSettingsApi;
  queryClient: QueryClient;
  reduxProxy: ReduxProxy;
  /** Auth bootstrap has no org on the action — connected wires Account. */
  resolveCurrentWorkspace: () => { db_schema: string } | null | undefined;
}): SystemSettingsServiceInstance {
  const { api, queryClient, reduxProxy, resolveCurrentWorkspace } = config;

  function seedCache(organizationId: string, rows: SystemSetting[]) {
    queryClient.setQueryData(systemSettingsQueryKey(organizationId), rows);
  }

  function rowsQuery(organizationId: string) {
    return {
      queryKey: systemSettingsQueryKey(organizationId),
      queryFn: () => api.getSettings({ organizationId }),
      staleTime: Infinity
    };
  }

  const unsubscribe = reduxProxy.read.addListener(event => {
    if (
      event.type !== 'LOAD_AUTH_SUCCESS' &&
      event.type !== 'UPDATE_SETTINGS_SUCCESS'
    ) {
      return;
    }
    const organizationId = resolveCurrentWorkspace()?.db_schema;
    if (!organizationId) {
      return;
    }
    if (event.type === 'UPDATE_SETTINGS_SUCCESS') {
      // Legacy settings pages save through `PUT /settings/:field_key`, which
      // this cache (staleTime: Infinity) would otherwise never learn about.
      if (isSettingName(event.field_key)) {
        void queryClient.invalidateQueries({
          queryKey: systemSettingsQueryKey(organizationId)
        });
      }
      return;
    }
    const result = event.result as {
      [SYSTEM_SETTINGS_WIRE_KEY]?: unknown;
    };
    const wire = toSystemSettingsWire(result[SYSTEM_SETTINGS_WIRE_KEY]);
    if (wire.length > 0) {
      seedCache(organizationId, wire);
    }
  });

  async function loadRows(scope: AppScopeIdentity): Promise<SystemSetting[]> {
    return queryClient.fetchQuery(rowsQuery(scope.organizationId));
  }

  const service: SystemSettingsService = {
    useSettings(scope: Maybe<AppScopeIdentity>) {
      return ensureIdleQuery(
        scope,
        useQuery(
          {
            enabled: maybe.isPresent(scope),
            ...rowsQuery(scope?.organizationId ?? ''),
            select: rows =>
              maybe.isPresent(scope) ? resolveAllSettings(rows, scope) : {}
          },
          queryClient
        )
      );
    },

    useSetting(scope: Maybe<AppScopeIdentity>, name: SettingName) {
      return ensureIdleQuery(
        scope,
        useQuery(
          {
            enabled: maybe.isPresent(scope),
            ...rowsQuery(scope?.organizationId ?? ''),
            select: rows =>
              maybe.isPresent(scope)
                ? resolveSettingValue(rows, scope, name)
                : undefined
          },
          queryClient
        )
      ) as ReturnType<SystemSettingsService['useSetting']>;
    },

    async ensureSettings(scope: AppScopeIdentity) {
      const rows = await loadRows(scope);
      return resolveAllSettings(rows, scope);
    },

    async ensureSetting(scope: AppScopeIdentity, name: SettingName) {
      const rows = await loadRows(scope);
      return resolveSettingValue(rows, scope, name);
    }
  };

  return Object.assign(service, {
    dispose: unsubscribe,
    seedCache
  });
}

export type SystemSettingsServiceInstance = SystemSettingsService & {
  dispose: () => void;
  seedCache: (organizationId: string, rows: SystemSetting[]) => void;
};
